import {
  DATE_OUTSIDE_SEASON_MANUAL_MESSAGE,
  NO_OFFERS_OUTSIDE_SEASON_MESSAGE,
  SKIPASS_EXTENDS_BEYOND_SEASON_MESSAGE,
} from '@/constants/inventory-domain.constant';
import { useBookSkiPassStore } from '@/store/book-ski-pass.store';
import {
  addCalendarDays,
  getSeasonEndFooterTextForVisibleMonth,
  isDayInAnySeasonalPeriod,
  mergeAdjacentSeasonalPeriods,
  parseTripStartingDate,
  type SeasonalPeriod,
} from '@/utils/domain-season';
import { useCallback, useMemo } from 'react';

const normalizePeriods = (raw: unknown): SeasonalPeriod[] =>
  Array.isArray(raw) ? (raw as SeasonalPeriod[]) : [];

export function useTripDetailsSeason() {
  const selectedDomain = useBookSkiPassStore((s) => s.selectedDomain);
  const referenceResponseData = useBookSkiPassStore((s) => s.referenceResponseData);
  const referenceDomainSeasonPeriods = useBookSkiPassStore((s) => s.referenceDomainSeasonPeriods);
  const manualSkiAreaSelection = useBookSkiPassStore((s) => s.manualSkiAreaSelection);
  const startingDate = useBookSkiPassStore((s) => s.stepTwoFormFieldsValues.startingDate);
  const daysOfSkiing = useBookSkiPassStore((s) => s.stepTwoFormFieldsValues.daysOfSkiing);

  const openedWithBookingRefDate = Boolean(referenceResponseData?.date);

  const periodsRaw: SeasonalPeriod[] = useMemo(() => {
    if (manualSkiAreaSelection) {
      return normalizePeriods(selectedDomain?.seasonal_periods);
    }

    const refPriorityId = referenceResponseData?.priority_domain?.id;
    if (refPriorityId != null) {
      if (referenceDomainSeasonPeriods != null && referenceDomainSeasonPeriods.length > 0) {
        return referenceDomainSeasonPeriods;
      }
      if (selectedDomain?.pk === refPriorityId) {
        return normalizePeriods(selectedDomain.seasonal_periods);
      }
    }

    return normalizePeriods(selectedDomain?.seasonal_periods);
  }, [
    manualSkiAreaSelection,
    referenceDomainSeasonPeriods,
    referenceResponseData?.priority_domain?.id,
    selectedDomain,
  ]);

  const periods = useMemo(() => mergeAdjacentSeasonalPeriods(periodsRaw), [periodsRaw]);

  const applies = periods.length > 0;

  const isStartDateAllowed = useCallback(
    (d: Date) => {
      if (!applies) return true;
      return isDayInAnySeasonalPeriod(d, periods);
    },
    [applies, periods]
  );

  const parsedStart = useMemo(() => parseTripStartingDate(startingDate), [startingDate]);

  const skiDays = useMemo(() => Number.parseInt(daysOfSkiing, 10), [daysOfSkiing]);

  const startOutsideSeason = useMemo(() => {
    if (!applies || !parsedStart) return false;
    return !isDayInAnySeasonalPeriod(parsedStart, periods);
  }, [applies, parsedStart, periods]);

  const showNoOffersForDateRange = openedWithBookingRefDate && startOutsideSeason;

  const showSkipassExtendsBeyondSeasonNotice = useMemo(() => {
    if (!applies || !parsedStart || startOutsideSeason) return false;
    if (!Number.isFinite(skiDays) || skiDays < 1) return false;
    const lastDay = addCalendarDays(parsedStart, skiDays - 1);
    return !isDayInAnySeasonalPeriod(lastDay, periods);
  }, [applies, parsedStart, periods, skiDays, startOutsideSeason]);

  const disallowedStartDateMessage = openedWithBookingRefDate
    ? NO_OFFERS_OUTSIDE_SEASON_MESSAGE
    : DATE_OUTSIDE_SEASON_MANUAL_MESSAGE;

  const getSeasonCalendarFooterText = useCallback(
    (year: number, monthIndex: number) =>
      applies ? getSeasonEndFooterTextForVisibleMonth(periods, year, monthIndex) : null,
    [applies, periods]
  );

  return {
    applies,
    isStartDateAllowed,
    getSeasonCalendarFooterText,
    showNoOffersForDateRange,
    showSkipassExtendsBeyondSeasonNotice,
    skipassBeyondSeasonCopy: SKIPASS_EXTENDS_BEYOND_SEASON_MESSAGE,
    disallowedStartDateMessage,
  };
}
